import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../core/lexicon';
import { budgetSpent, canStep, cost, meterSegments, pointsComplete, surcharge } from '../core/ballot';
import { seq } from '../core/util';
import { useVoting } from './useVoting';
import { ListenLink, VotingClosed, VotingLoading, votingRight } from './ballotShared';
import { Screen, TopBar } from './Layout';

/**
 * Point allocation. The budget meter is the tuned visual the spec says to keep
 * exactly: solid segments for points delivered, hatched for the doubling
 * surcharge. Every number (budget, range, multiplier) comes from the economy, so
 * a ruleset change needs no edit here. A duplicate cannot be marked down (rule 5b).
 */
export function Vote() {
  const t = useT();
  const nav = useNavigate();
  const v = useVoting();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const title = t('phase.points.name');
  if (v.loading) return <VotingLoading title={title} />;
  if (!v.round || v.round.phase !== 'VOTING') return <VotingClosed t={t} title={title} round={v.round} />;

  const { eco, draft } = v;
  const segs = meterSegments(draft, eco);
  const spent = budgetSpent(draft, eco);
  const sur = surcharge(draft, eco);
  const left = eco.budget - spent;
  const complete = pointsComplete(draft, eco);

  async function send() {
    setBusy(true);
    setProblem(null);
    setNote(null);
    const res = await v.save();
    if (res.error) {
      setProblem(res.error);
      setBusy(false);
    } else if (res.submitted) {
      nav('/'); // whole ballot locked in
    } else {
      // Points saved, but the ballot isn't in until veto + line-up are done too.
      setNote(t('ballot.draft'));
      setBusy(false);
    }
  }

  return (
    <Screen>
      <TopBar title={t('round.label', { n: v.round.sequence_no })} sub={title} right={votingRight(v.round)} />
      <ListenLink t={t} round={v.round} />

      <section className="budget" aria-live="polite">
        <div className="b-head">
          <span className="b-l">{t('budget.label').toUpperCase()}</span>
          <span className="b-c">
            {spent}/{eco.budget}
          </span>
        </div>
        <div className="segs" role="img" aria-label={t('budget.label')}>
          {segs.map((s, i) => (
            <div key={i} className={`seg${s === 'spent' ? ' spent' : s === 'pen' ? ' pen' : ''}`} />
          ))}
        </div>
        <p className="b-n">
          {sur > 0 ? t('budget.surcharge', { n: sur }) : t('budget.rule', { n: eco.budget })}
        </p>
      </section>

      <div className="list">
        {v.votable.map((tr) => {
          const p = draft.get(tr.submission_id)?.points ?? 0;
          const dupFloor = tr.is_duplicate; // no negatives on a duplicate
          const canInc = canStep(draft, tr.submission_id, 1, eco);
          const canDec = canStep(draft, tr.submission_id, -1, eco) && !(dupFloor && p <= 0);
          return (
            <article key={tr.submission_id} className={`track${p < 0 ? ' neg' : ''}`}>
              <div className="n">{seq(tr.display_order)}</div>
              <h2 className="t2">{tr.title}</h2>
              <p className="a">
                {tr.subtitle ?? ''}
                {tr.is_duplicate ? ` · ${t('duplicate.flag').toUpperCase()}` : ''}
              </p>
              <div className="step">
                <button
                  className="sb"
                  aria-label={`Fewer points for ${tr.title}`}
                  disabled={!canDec}
                  onClick={() => v.update(tr.submission_id, { points: p - 1 })}
                >
                  −
                </button>
                <span className="val">{p > 0 ? '+' + p : p}</span>
                <button
                  className="sb"
                  aria-label={`More points for ${tr.title}`}
                  disabled={!canInc}
                  onClick={() => v.update(tr.submission_id, { points: p + 1 })}
                >
                  +
                </button>
                <span className="cost">{p < 0 ? `COSTS ${cost(p, eco)}` : ''}</span>
              </div>
            </article>
          );
        })}
      </div>

      <div className="act">
        {problem ? <div className="errline" style={{ marginBottom: 8 }}>{problem}</div> : null}
        {note ? <div className="errline" style={{ marginBottom: 8, background: 'transparent', color: 'var(--k1)' }}>{note}</div> : null}
        <button className="go" disabled={!complete || busy} onClick={send}>
          {busy ? '…' : complete ? t('phase.points.cta').toUpperCase() : t('budget.remaining', { n: left }).toUpperCase()}
        </button>
      </div>
    </Screen>
  );
}
